import { ArrowUpRight, LogOut, Search } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router";
import type { ClientSession } from "../../api/auth";
import { isNavActive, type SiteNavItem } from "./site-nav-data";
import { useSiteSearch } from "./useSiteSearch";

type SiteMobileMenuProps = {
  id: string;
  open: boolean;
  items: SiteNavItem[];
  session: ClientSession;
  loading: boolean;
  onClose: () => void;
  onLogout: () => void;
};

/** 移动端全屏菜单：大字号导航逐行出现，Esc 关闭，打开时锁定页面滚动并把焦点移到第一项。 */
export function SiteMobileMenu({ id, open, items, session, loading, onClose, onLogout }: SiteMobileMenuProps) {
  const { pathname } = useLocation();
  const panelRef = useRef<HTMLDivElement>(null);
  const [keyword, setKeyword] = useState("");
  const search = useSiteSearch();
  const submit = (event: FormEvent) => {
    event.preventDefault();
    search(keyword);
    setKeyword("");
  };

  useEffect(() => {
    if (!open) return;
    const root = document.documentElement;
    const previous = root.style.overflow;
    root.style.overflow = "hidden";
    panelRef.current?.querySelector<HTMLElement>("a, button")?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      root.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    onClose();
  }, [pathname, onClose]);

  return (
    <div
      ref={panelRef}
      id={id}
      className={`site-menu${open ? " is-open" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label="站点菜单"
      aria-hidden={!open}
      inert={!open}
    >
      <div className="site-menu-body">
        <form className="site-menu-search" role="search" onSubmit={submit}>
          <Search size={18} strokeWidth={1.9} aria-hidden="true" />
          <input
            type="search"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索模型、材质、纹理…"
            aria-label="搜索素材库"
            enterKeyHint="search"
          />
        </form>
        <nav className="site-menu-links" aria-label="主导航">
          {items.map((item, index) => (
            <Link
              key={item.id}
              to={item.to}
              aria-current={isNavActive(item, pathname) ? "page" : undefined}
              style={{ transitionDelay: open ? `${80 + index * 60}ms` : "0ms" }}
            >
              {item.label}
              <ArrowUpRight size={22} strokeWidth={1.6} aria-hidden="true" />
            </Link>
          ))}
        </nav>
      </div>
      <div className="site-menu-foot">
        {loading ? <span className="site-menu-status">正在验证账户…</span> : session.id ? (
          <>
            <Link className="site-start" to="/workbench">进入工作台</Link>
            <button type="button" className="site-menu-logout" onClick={onLogout}><LogOut size={15} />退出登录</button>
          </>
        ) : (
          <>
            <Link className="site-start" to="/moodboard">开始创作</Link>
            <Link className="site-login" to="/login">登录 / 注册</Link>
          </>
        )}
      </div>
    </div>
  );
}
